import React, { useState, useEffect } from 'react';
import InputField from '../components/ui/InputField';
import { MdEmail, MdLock, MdPerson, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { BiLoaderAlt } from 'react-icons/bi';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../axios';
import toast from 'react-hot-toast';
import Logo from '../assets/manak-logo.png';

import Carousel_1 from '../assets/carousel_1.png';
import Carousel_2 from '../assets/carousel_2.png';
import Carousel_3 from '../assets/carousel_3.png';
import Carousel_4 from '../assets/carousel_4.png';

const AdminSignup = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [signupFormData, setSignupFormData] = useState({
    email: '',
    password: '',
    fullname: '',
    designation: 'superAdmin', // Always set to superAdmin but not displayed in form
    profilePicture: '', // Optional field, not displayed in form
  });
  const [signupError, setSignupError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSignupForm, setShowSignupForm] = useState(false);
  const isMobile = window.innerWidth < 768;

  const navigate = useNavigate();

  // Define carousel content
  const carouselItems = [
    {
      id: 0,
      title: 'Secure Admin Registration',
      image: Carousel_1,
      description: 'Create your administrative account securely',
    },
    {
      id: 1,
      title: 'Manage Your System',
      image: Carousel_2,
      description: 'Full control over your project management system',
    },
    {
      id: 2,
      title: 'Administrative Access',
      image: Carousel_3,
      description: 'Complete oversight of projects and team members',
    },
    {
      id: 3,
      title: 'Secure & Protected',
      image: Carousel_4,
      description: 'Enterprise-level security for your admin account',
    },
  ];

  // Auto-run slider
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide(prev => (prev + 1) % carouselItems.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Handle form input changes
  const handleInputChange = (field: string, value: string) => {
    setSignupFormData({
      ...signupFormData,
      [field]: value,
    });
  };

  // Handle signup form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSignupError('');
    setIsLoading(true);

    // Validate form
    if (!signupFormData.email || !signupFormData.password || !signupFormData.fullname) {
      setSignupError('Please fill in all required fields');
      setIsLoading(false);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(signupFormData.email)) {
      setSignupError('Please enter a valid email address');
      setIsLoading(false);
      return;
    }

    // Validate password length
    if (signupFormData.password.length < 8) {
      setSignupError('Password must be at least 8 characters long');
      setIsLoading(false);
      return;
    }

    try {
      const response = await axiosInstance.post('/v1/admin/signup', {
        email: signupFormData.email,
        password: signupFormData.password,
        fullname: signupFormData.fullname,
        designation: 'superAdmin', // Always send superAdmin
        // profilePicture is optional and can be empty
      });

      if (response.data.success) {
        toast.success('Admin account created successfully!');
        toast.success('Please wait for approval from an existing admin to access the system.', {
          duration: 6000,
        });
        // Redirect to login page after successful signup
        setTimeout(() => {
          navigate('/auth/login');
        }, 3000);
      } else {
        setSignupError(response.data.message || 'Signup failed');
      }
    } catch (err: any) {
      console.error('Signup failed:', err);
      if (err.response?.data?.errors) {
        const errorMessages = err.response.data.errors.map((error: any) => error.message).join(', ');
        setSignupError(errorMessages);
      } else {
        setSignupError(err.response?.data?.message || 'Signup failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const mobileCarouselData = [
    {
      title: 'Secure Admin Registration',
      image: Carousel_1,
      description: 'Create your administrative account securely',
    },
    {
      title: 'Manage Your System',
      image: Carousel_2,
      description: 'Full control over your project management system',
    },
    {
      title: 'Administrative Access',
      image: Carousel_3,
      description: 'Complete oversight of projects and team members',
    },
    {
      title: 'Secure & Protected',
      image: Carousel_4,
      description: 'Enterprise-level security for your admin account',
    },
  ];

  const MobileCarousel = () => (
    <div className="flex flex-col min-h-[500px] justify-between">
      <div className="flex-1">
        {/* Add Logo */}
        <div className="flex justify-center mb-6">
          <img src={Logo} alt="Manak Logo" className="h-12" />
        </div>

        <h2 className="text-2xl font-bold text-red-600 mb-6 text-center">
          Admin Registration
        </h2>

        {/* Carousel Container */}
        <div className="relative h-[300px] mb-6">
          {mobileCarouselData.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-all duration-500 flex flex-col ${activeSlide === index ? 'opacity-100 z-10' : 'opacity-0 z-0'
                }`}
            >
              <div className="w-full h-48 flex items-center justify-center mb-4">
                <img
                  src={slide.image}
                  alt={slide.title}
                  className="max-w-full max-h-full object-contain rounded-lg"
                />
              </div>
              <div className="text-center px-4">
                <h3 className="text-xl font-bold mb-2">{slide.title}</h3>
                <p className="text-gray-600 text-sm">{slide.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Dots Navigation */}
        <div className="flex justify-center gap-2 mb-8">
          {mobileCarouselData.map((_, index) => (
            <button
              key={index}
              className={`w-2 h-2 rounded-full ${activeSlide === index ? 'bg-red-600' : 'bg-gray-300'
                }`}
              onClick={() => setActiveSlide(index)}
            />
          ))}
        </div>
      </div>

      <button
        onClick={() => setShowSignupForm(true)}
        className="w-full bg-red-600 text-white py-3 rounded-lg text-base font-semibold mt-auto"
      >
        Create Admin Account
      </button>
    </div>
  );

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-white relative overflow-hidden">
      {/* Background Pattern SVG */}
      <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern
            id="wave-pattern"
            patternUnits="userSpaceOnUse"
            width="100"
            height="8"
            patternTransform="rotate(0)"
          >
            <path
              d="M0,4 C12.5,0 12.5,8 25,4 C37.5,0 37.5,8 50,4 C62.5,0 62.5,8 75,4 C87.5,0 87.5,8 100,4"
              fill="none"
              stroke="rgba(232, 180, 180, 1)"
              strokeWidth="0.6"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#wave-pattern)" />
      </svg>

      {/* Main Content */}
      <div className="flex flex-col md:flex-row w-full max-w-6xl px-4 md:px-8 z-10">
        {/* Left Side - Show only on desktop */}
        {!isMobile && (
          <div className="w-full md:w-1/2 pr-0 md:pr-8 flex flex-col items-center justify-center mb-8 md:mb-0">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center w-full">
              <span className="text-red-600">
                {carouselItems[activeSlide].title.split(' ').slice(0, 2).join(' ')}
              </span>
              <br />
              <span className="text-red-600">
                {carouselItems[activeSlide].title.split(' ').slice(2).join(' ')}
              </span>
            </h1>

            <div className="mt-4 md:mt-8 w-full max-w-md relative overflow-hidden">
              {/* Slider container */}
              <div className="relative w-full h-96">
                {carouselItems.map(item => (
                  <div
                    key={item.id}
                    className={`absolute inset-0 transition-opacity duration-1000 ${activeSlide === item.id ? 'opacity-100' : 'opacity-0'}`}
                  >
                    <div className="relative h-full flex items-center justify-center">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-contain max-h-96"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Dots Navigation */}
            <div className="flex items-center justify-center mt-6 self-center">
              {carouselItems.map(item => (
                <div
                  key={item.id}
                  className={`h-1 rounded-full mx-1 transition-all duration-300 cursor-pointer ${activeSlide === item.id ? 'w-8 bg-red-600' : 'w-4 bg-gray-300'
                    }`}
                  onClick={() => setActiveSlide(item.id)}
                ></div>
              ))}
            </div>
          </div>
        )}

        {/* Right Side / Mobile Full Width */}
        <div className={`w-full ${!isMobile ? 'md:w-1/2' : ''} flex justify-center`}>
          <div className="bg-white p-6 md:p-10 rounded-2xl shadow-lg w-full max-w-xl">
            {isMobile && !showSignupForm ? (
              <MobileCarousel />
            ) : (
              // Signup Form View
              <>
                {isMobile && (
                  <button
                    onClick={() => setShowSignupForm(false)}
                    className="mb-4 text-gray-600 flex items-center text-sm"
                  >
                    ← Back
                  </button>
                )}
                <div className={`${isMobile ? 'animate-slide-left' : ''}`}>
                  <h2 className="text-2xl md:text-4xl font-bold text-red-600 mb-2 md:mb-4">
                    Admin Registration
                  </h2>
                  <h3 className="text-xl md:text-3xl font-semibold text-red-600 mb-3 md:mb-6">
                    Create your admin account
                  </h3>

                  <p className="text-gray-500 mb-6 md:mb-8">Secure access to manage your system!</p>

                  {/* Display error message if any */}
                  {signupError && (
                    <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
                      {signupError}
                    </div>
                  )}

                  <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                      <InputField
                        label="Full Name"
                        value={signupFormData.fullname}
                        onChange={e => handleInputChange('fullname', e.target.value)}
                        placeholder="Enter your full name"
                        icon={<MdPerson className="w-5 h-5" />}
                        labelClassName="text-red-600"
                      />
                    </div>

                    <div className="mb-4">
                      <InputField
                        label="Email"
                        type="email"
                        value={signupFormData.email}
                        onChange={e => handleInputChange('email', e.target.value)}
                        placeholder="Enter your email"
                        icon={<MdEmail className="w-5 h-5" />}
                        labelClassName="text-red-600"
                      />
                    </div>

                    <div className="mb-4 relative">
                      <InputField
                        label="Password"
                        type="password"
                        value={signupFormData.password}
                        onChange={e => handleInputChange('password', e.target.value)}
                        placeholder="Enter your password (min 8 characters)"
                        icon={<MdLock className="w-5 h-5" />}
                        labelClassName="text-red-600"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading}
                      className={`w-full bg-red-600 text-white py-4 rounded-lg flex items-center justify-center font-medium text-lg ${isLoading ? 'opacity-70 cursor-not-allowed' : ''
                        }`}
                    >
                      {isLoading ? (
                        <>
                          <BiLoaderAlt className="animate-spin -ml-1 mr-3 h-5 w-5" />
                          CREATING ACCOUNT...
                        </>
                      ) : (
                        <>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-6 w-6 mr-2"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          CREATE ADMIN ACCOUNT
                        </>
                      )}
                    </button>
                  </form>

                  <div className="mt-6 text-center">
                    <p className="text-gray-600">
                      Already have an account?{' '}
                      <button
                        onClick={() => navigate('/auth/login')}
                        className="text-red-600 font-medium hover:underline"
                      >
                        Sign in here
                      </button>
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSignup;
